import fetch from '@zapier/identity-fetch';
import { useEffect, useState } from 'react';

import { urls } from '@zapier/identity-utils';
import {
  ErrorResponse,
  DeclineInvitationErrorResponse,
  Invitation,
  InvitationLoadState,
  InvitationResponse,
} from './types';

async function getInvitationByCode(code: string): Promise<Invitation> {
  // Get invitation from API
  const result = await fetch<InvitationResponse | ErrorResponse>(
    `${urls.INVITATION_API_URL}/${code}/`
  );

  if (!result.ok) {
    const body = result.body as ErrorResponse;
    // Oh no! Something went wrong

    // Some errors are just returned as strings
    if (typeof body === 'string') {
      throw body;
    }

    // Look for possible error properties
    throw body?.error || body?.detail;
  }

  // Success
  const body = result.body as InvitationResponse;

  return {
    code: body.code,
    account: {
      id: body.account.id,
      logoUrl: body.account.logo_url,
      name: body.account.name,
      hasSamlSso: body.account.has_saml_sso,
    },
    email: body.email,
    emailFound: body.email_found || null,
    id: body.id,
    status: body.status,
    ssoUrl: body.sso_url,
    createdBy: body.created_by,
  };
}

async function declineInvitation(code: string): Promise<Invitation> {
  const result = await fetch<
    InvitationResponse | DeclineInvitationErrorResponse
  >(`${urls.INVITATION_API_URL}/${code}/decline`, { method: 'PUT' });

  if (!result.ok) {
    const body = result.body as DeclineInvitationErrorResponse;
    // Oh no! Something went wrong

    // Some errors are just returned as strings
    if (typeof body === 'string') {
      throw body;
    }

    // Look for possible error properties
    throw body?.error || body?.message;
  }

  // Success
  const body = result.body as InvitationResponse;

  return {
    account: {
      id: body.account.id,
      logoUrl: body.account.logo_url,
      name: body.account.name,
      hasSamlSso: body.account.has_saml_sso,
    },
    email: body.email,
    emailFound: body.email_found || null,
    id: body.id,
    code: body.code,
    status: body.status,
    createdBy: body.created_by,
    ssoUrl: body.sso_url,
  };
}

type Options = {
  skip?: boolean;
};

/**
 * Given an invitation based on the given invitation code
 */
export function useInvitation(code: string | undefined, options?: Options) {
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [status, setStatus] = useState<InvitationLoadState>('initial');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function maybeGetInvitation() {
      if (code && !options?.skip) {
        // Show we are loading something
        setStatus('loading');

        try {
          // Try to get invitation
          const invitation = await getInvitationByCode(code);

          // We got an invitation
          setInvitation(invitation);
          setError(null);
          setStatus('success');
        } catch (err) {
          setError(err as string);
          setStatus('error');
        }
      } else {
        // we didn't get a code so clear invitation
        setInvitation(null);

        setStatus('initial');
      }
    }

    maybeGetInvitation();
  }, [code, options?.skip]);

  async function maybeDeclineInvitation(invitationCode: string) {
    try {
      // Try to decline invitation
      const invitation = await declineInvitation(invitationCode);

      // Update the invitation
      setInvitation(invitation);
      setError(null);
      setStatus('success');
    } catch (err) {
      setError(err as string);
      setStatus('error');
    }
  }

  return {
    code,
    error,
    invitation,
    isValid: Boolean(
      // If there wasn't a code passed then we consider it valid since we aren't looking for an invitation
      !code ||
        // If we haven't finished loading the invite then it can't be invalid yet
        status === 'initial' ||
        status === 'loading' ||
        // If we have an error then the invite is invalid
        // If the invitation status isn't 'pending' it means it's either
        // already been used or has expired so it's invalid
        (!error && invitation?.status === 'pending')
    ),
    declineInvitation: maybeDeclineInvitation,
    status,
  };
}
